<template>
  <div class="container">
    <div class="content-block-large">
      <div class="swiper__container simple-slider" data-theme="summer-sky">
        <ClientOnly>
          <SlidersSliderNavigation @previous="swiper.prev()" @next="swiper.next()" />
          <swiper-container
            ref="containerRef"
            :slides-per-view="1"
            :loop="true"
            :auto-height="true">
            <swiper-slide
              v-for="(person, index) in data"
              :key="person.phoneNumber?.value ?? index"
              class="swiper__wrapper">
              <div class="swiper__slide">
                <div class="slide">
                  <div class="slide__content">
                    <figure class="avatar">
                      <img
                        v-if="person.profilePicture && person.profilePicture.value?.url"
                        v-bind="useEpiAttrs(person.profilePicture)"
                        width="150"
                        loading="lazy"
                        :src="person.profilePicture.value?.url + '?w=150'"
                        :alt="person.profilePicture.value?.alt ?? ''" />
                    </figure>
                    <q class="slide__quote">
                      <h2 v-if="person.quote" v-bind="useEpiAttrs(person.quote)">
                        {{ person.quote.value }}
                      </h2>
                    </q>
                    <p class="slide__detail" />
                    <figure class="badge">
                      <img
                        v-if="icon && icon.value && icon.value.url"
                        v-bind="useEpiAttrs(icon)"
                        width="150"
                        loading="lazy"
                        :src="icon.value.url + '?w=150'"
                        :alt="icon.value.alt ?? ''" />
                    </figure>
                    <div class="slide__person">
                      <h3 v-if="person.firstName" v-bind="useEpiAttrs(person.firstName)">
                        {{ person.firstName.value }}
                      </h3>
                      <em v-if="person.jobTitle" v-bind="useEpiAttrs(person.jobTitle)">{{
                        person.jobTitle.value
                      }}</em>
                    </div>
                    <LinksIconTextLink
                      class="read-more"
                      text="lees verder"
                      icon="arrow-right"
                      :link="`/onze-collegas/#${person.firstName?.value ?? ''}`" />
                  </div>
                  <div class="slide__img">
                    <img
                      v-if="person.image?.value?.url"
                      v-bind="useEpiAttrs(person.image)"
                      loading="lazy"
                      width="790"
                      :src="person.image.value?.url + '?w=790'"
                      :alt="person.image.value.alt ?? ''" />
                  </div>
                </div>
              </div>
            </swiper-slide>
          </swiper-container>
          <template #fallback>
            <div :style="{ height: '576px' }" />
          </template>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { FieldResponseModelOfImageResponseModel, PersonBlockResponseModel } from '@api';

defineProps<{
  data: PersonBlockResponseModel[];
  icon?: FieldResponseModelOfImageResponseModel | null;
}>();

const containerRef = ref(null);
const swiper = useSwiper(containerRef);
</script>

<style lang="scss" scoped>
.content-block-large {
  padding: 0;

  @include media-breakpoint-up(lg) {
    padding: 0 0 px(30);
  }
}

.swiper {
  &__container {
    position: relative;
    overflow: hidden;
    list-style: none;
    padding: 0;
    z-index: 1;
    margin-inline: auto;
    height: auto;
    color: var(--theme-color);
    background-color: var(--theme-bg-color);

    &.simple-slider {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      display: block;
    }
  }

  &__wrapper {
    position: relative;
    display: flex;
    align-items: flex-start;
    width: 100%;
    height: auto;
    z-index: 1;
    box-sizing: content-box;
  }

  &__slide {
    position: relative;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    .slide {
      display: flex;
      flex-direction: column;
      height: auto;
      padding: px(16) px(20) px(32);

      &__img {
        position: relative;
        order: 1;
        align-self: flex-start;
        margin-bottom: px(12);

        img {
          max-width: 100%;
          height: auto;
          border-radius: px(12);
        }
      }

      &__content {
        display: grid;
        grid-template-columns: px(80) 1fr;
        gap: px(8);
        order: 2;
        grid-template-areas:
          'slideQuote slideQuote'
          'slideDetail slideDetail'
          '. slideMoreLink'
          'slideBadge slidePerson';

        .avatar {
          display: none;
          grid-area: slideAvatar;
          width: px(80);
          height: px(80);
          margin-bottom: px(12);
        }

        .badge {
          grid-area: slideBadge;
          width: px(80);
          height: px(80);
          background-color: var(--color-white);
          overflow: hidden;
          border-radius: 50%;
          border: px(2) solid var(--color-white);

          img {
            width: 100%;
            height: 100%;
          }
        }

        .read-more {
          grid-area: slideMoreLink;
          justify-self: flex-end;
          margin-bottom: px(8);
        }
      }

      &__quote {
        grid-area: slideQuote;
        align-self: center;

        > *::before {
          content: '“';
        }

        > *::after {
          content: '”';
        }
      }

      &__detail {
        grid-area: slideDetail;
      }

      &__person {
        grid-area: slidePerson;
        align-self: center;

        h3 {
          margin-bottom: 0;
        }
      }
    }
  }

  @include media-breakpoint-up(lg) {
    &__wrapper {
      min-height: px(576);
    }

    &__container {
      &.simple-slider {
        border-radius: px(12);
      }
    }

    &__slide {
      .slide {
        justify-content: flex-end;
        flex-direction: row;
        padding: 0;

        &__img {
          background-color: var(--color-bg-blue);
          margin-bottom: 0;
          width: 50%;
          order: 2;
          align-self: stretch;

          img {
            position: absolute;
            border-radius: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            inset: 0;
          }
        }

        &__content {
          width: 50%;
          grid-template:
            'slideAvatar .'
            'slideQuote slideQuote'
            'slideDetail slideDetail'
            'slideBadge slidePerson'
            '. slideMoreLink';
          grid-template-rows: auto auto 1fr auto auto;
          grid-template-columns: px(80) 1fr;
          gap: px(24);
          padding: px(64) px(64) px(64) px(76);
          order: 1;

          .avatar {
            display: block;
          }
        }
      }
    }
  }

  @include media-breakpoint-up(xl) {
    &__slide {
      .slide {
        &__content {
          grid-template-columns: px(150) 1fr;
          grid-template-areas:
            'slideAvatar slideQuote'
            '. slideDetail'
            'slideBadge slidePerson'
            '. slideMoreLink';
          padding-left: px(160);

          .avatar {
            width: px(150);
            height: px(150);
          }

          .badge {
            width: px(150);
            height: px(150);
          }
        }
      }
    }
  }
}
</style>
